import { useQuery } from '@apollo/react-hooks';
import { USER_AUTH_ROLES_PRIVILEGES } from 'graphql/entry';

// Cache duration in milliseconds (5 minutes)
const CACHE_DURATION = 5 * 60 * 1000;

// Debug mode flag - can be controlled via environment variable or config
const DEBUG_MODE = process.env.REACT_APP_DEBUG_AUTH_PRIVILEGE === 'true';

interface Props {
  privilege: string | string[],
  debug?: boolean, // Optional debug flag per hook instance
}

interface Args {
  privilegesFromUser: Array<{ name: string }>,
  matchTo: string | string[],
}

interface HookReturn {
  loading: boolean,
  hasPrivilege: boolean,
  privileges: string[],
  error: any,
  debug?: {
    cacheHit: boolean,
    cacheAge: number | null,
    timestamp: number,
    matchedPrivileges: string[],
    allPrivileges: Array<{ name: string }>,
  },
}

/**
 * Debug logger that only logs when debug mode is enabled
 */
const debugLog = (message: string, data?: any) => {
  if (DEBUG_MODE) {
    console.log(`[AuthPrivilege Debug] ${message}`, data || '');
  }
};

/**
 * Match a single or list of privileges to the user.
 * @param privilegesFromUser User privileges array.
 * @param matchTo A single or list of privilages to match against the user.
 */
function matchPrivilegeToUser({ privilegesFromUser = [], matchTo }: Args): string[] {
  return privilegesFromUser
    .filter((p) => Array.isArray(matchTo) ? matchTo.includes(p.name) : p.name === matchTo)
    .map((n) => n.name);
}

/**
 * @typedef {HookReturn} HookReturn
 * @property {boolean} loading - The useQuery loading state.
 * @property {boolean} hasPrivilege - Are there matching privilege(s).
 * @property {boolean} privileges - The list of matching privileges. Useful for handling multiple checks on
 * a single tool or component.
 * @property {boolean} error - The useQuery error state.
 * @property {Object} debug - Debug information (only available when debug mode is enabled)
 *
 * Get the privileges for the currently logged in user.
 * - Cache-first policy with a 5-minute cache duration
 * - Results are cached and reused across components
 * @param {string|string[]} privilege A privilage name or list of names.
 * @param {boolean} debug Enable debug mode for this hook instance
 * @returns {HookReturn}
 */
export const useAuthPrivilege = ({ privilege, debug = false }: Props): HookReturn => {
  const isDebugEnabled = DEBUG_MODE || debug;
  
  const { data, loading, error } = useQuery(USER_AUTH_ROLES_PRIVILEGES, {
    fetchPolicy: 'cache-first',
    context: {
      // Add cache metadata
      cacheMetadata: {
        timestamp: Date.now(),
        duration: CACHE_DURATION,
      },
    },
  });

  const privilegesFromUser = data?.context?.user?.allPrivileges;
  const privileges = matchPrivilegeToUser({ privilegesFromUser, matchTo: privilege });

  // Debug information
  const debugInfo = isDebugEnabled ? {
    cacheHit: !loading && !!data,
    cacheAge: data ? Date.now() - (data?.context?.cacheMetadata?.timestamp || Date.now()) : null,
    timestamp: Date.now(),
    matchedPrivileges: privileges,
    allPrivileges: privilegesFromUser || [],
  } : undefined;

  if (isDebugEnabled) {
    debugLog('Privilege Check', {
      requested: privilege,
      matched: privileges,
      cacheHit: debugInfo?.cacheHit,
      cacheAge: debugInfo?.cacheAge,
    });
  }

  return {
    loading,
    hasPrivilege: privileges.length > 0,
    privileges,
    error,
    ...(isDebugEnabled && { debug: debugInfo }),
  }
}
